/**
 * Status Legend
 *
 * What the state badge on a card means, as a dialog opened from a `?` beside
 * the badge itself. Reference material rather than something to read every
 * visit: it is only wanted at the moment a state is unfamiliar, and that moment
 * is while looking at the badge.
 *
 * Built from `RESOURCE_STATES`, the same table the badges take their hue and
 * pulse from, so the two cannot disagree.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';

import StatusBadge from '@/shared/components/StatusBadge';
import { paletteHue } from '@/shared/utils/paletteHues';

import { RESOURCE_STATES } from '../resourceStates';

/** The list itself, so the dialog below is the only thing that positions it. */
export function ResourceStatusLegend() {
  return (
    /* One per row, in launch order: read down the column and you have the
       sequence a game walks through, which is most of what the legend is for.
       Columns would fill left-to-right and break that reading order. */
    <Stack sx={{ gap: 1 }}>
      {RESOURCE_STATES.map((info) => (
        /* alignItems flex-start, not center: the descriptions wrap to two
           lines at narrow widths and a centred badge then floats. */
        <Stack
          key={info.state}
          direction="row"
          sx={{ gap: 1, alignItems: 'flex-start' }}
        >
          {/* Fixed width, sized to the longest label (READY FOR BROADCAST):
              stacked in one column the badges would otherwise start every
              description at a different x, which reads as misalignment.

              The label is the same text the card's badge renders — underscores
              swapped for spaces, uppercased by StatusBadge — so a state on a
              card is matched to its row by sight rather than by translation. */}
          <Box sx={{ flexShrink: 0, minWidth: 170, pt: '1px' }}>
            <StatusBadge
              label={info.state.replace(/_/g, ' ')}
              color={paletteHue(info.tone)}
              pulse={Boolean(info.inFlight)}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', lineHeight: 1.4 }}
          >
            {info.description}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The `?` beside a status badge, and the dialog it opens.
 *
 * Owns its own open state so a card renders one element and nothing has to be
 * threaded down from the page. Every card gets its own dialog instance, which
 * costs nothing while closed and avoids a shared-state layer for a component
 * whose whole job is to be self-contained.
 */
export function ResourceStatusLegendButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="What do these statuses mean?">
        <IconButton
          size="small"
          aria-label="Status legend"
          onClick={() => setOpen(true)}
          sx={{ color: 'text.secondary' }}
        >
          <HelpOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Status legend</DialogTitle>
        <DialogContent dividers>
          <ResourceStatusLegend />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
